import { useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Pause, Play, Square, Trash2, Bot, Zap, DollarSign, MessageSquare, Activity,
} from 'lucide-react';
import { useTeam, useTeamMessages, useStopTeam, usePauseTeam, useResumeTeam, useDeleteTeam, useSendMessage } from '../hooks/useTeams';
import { usePauseInstance, useResumeInstance, useStopInstance } from '../hooks/useInstances';
import { TeamStatus } from '../types/team';
import { InstanceStatus } from '../types/instance';
import InstanceTerminal from '../components/InstanceTerminal';
import TeamMessages from '../components/TeamMessages';
import TeamActivity from '../components/TeamActivity';
import type { TeamActivityEvent } from '../components/TeamActivity';

const TEAM_STATUS_STYLES: Record<TeamStatus, string> = {
  [TeamStatus.ACTIVE]: 'bg-green-100 text-green-700',
  [TeamStatus.PAUSED]: 'bg-yellow-100 text-yellow-700',
  [TeamStatus.COMPLETED]: 'bg-slate-100 text-slate-600',
  [TeamStatus.FAILED]: 'bg-red-100 text-red-700',
};

const INSTANCE_STATUS_DOT: Record<string, string> = {
  [InstanceStatus.RUNNING]: 'bg-green-500',
  [InstanceStatus.PAUSED]: 'bg-yellow-500',
  [InstanceStatus.STARTING]: 'bg-blue-500',
  [InstanceStatus.PENDING]: 'bg-blue-400',
  [InstanceStatus.COMPLETED]: 'bg-slate-400',
  [InstanceStatus.STOPPED]: 'bg-slate-400',
  [InstanceStatus.FAILED]: 'bg-red-500',
};

type BottomTab = 'activity' | 'messages';

export default function TeamDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: team, isLoading } = useTeam(id);
  const { data: messages } = useTeamMessages(id);
  const stopTeam = useStopTeam();
  const pauseTeam = usePauseTeam();
  const resumeTeam = useResumeTeam();
  const deleteTeam = useDeleteTeam();
  const sendMessage = useSendMessage(id ?? '');
  const pauseInstance = usePauseInstance();
  const resumeInstance = useResumeInstance();
  const stopInstance = useStopInstance();
  const [bottomTab, setBottomTab] = useState<BottomTab>('messages');

  const instances = team?.instances ?? [];

  const activityEvents: TeamActivityEvent[] = useMemo(() => {
    const all: TeamActivityEvent[] = [];
    for (const inst of instances) {
      const agentName = inst.agent?.name ?? 'Unknown';
      for (const ev of inst.recentEvents ?? []) {
        all.push({ ...ev, agentName });
      }
    }
    return all.sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
  }, [instances]);

  if (isLoading) {
    return <div className="p-8 text-sm text-slate-400">Loading team...</div>;
  }

  if (!team) {
    return <div className="p-8 text-sm text-slate-500">Team not found.</div>;
  }

  const gridCols = instances.length <= 2 ? 'grid-cols-1 lg:grid-cols-2' : 'grid-cols-1 lg:grid-cols-2 xl:grid-cols-3';

  const handleStopTeam = () => stopTeam.mutate(team.id);
  const handlePauseTeam = () => pauseTeam.mutate(team.id);
  const handleResumeTeam = () => resumeTeam.mutate(team.id);
  const handleDeleteTeam = () => {
    if (confirm('Delete this team? This cannot be undone.')) {
      deleteTeam.mutate(team.id);
      navigate(`/projects/${team.projectId}`);
    }
  };

  const instanceOptions = instances.map((inst) => ({
    id: inst.id,
    name: inst.agent?.name ?? 'Unknown',
  }));

  return (
    <div className="flex h-full flex-col overflow-hidden">
      {/* Header */}
      <div className="shrink-0 border-b border-slate-200 px-6 py-4">
        <button
          onClick={() => navigate(`/projects/${team.projectId}`)}
          className="mb-3 flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to project
        </button>

        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <h1 className="text-xl font-bold text-slate-900">{team.name}</h1>
            <span
              className={`rounded-full px-2.5 py-0.5 text-xs font-medium capitalize ${TEAM_STATUS_STYLES[team.status] ?? 'bg-slate-100 text-slate-600'}`}
            >
              {team.status}
            </span>
          </div>
          <div className="flex items-center gap-2">
            {team.status === TeamStatus.ACTIVE && (
              <>
                <button
                  onClick={handlePauseTeam}
                  disabled={pauseTeam.isPending}
                  className="flex items-center gap-1.5 rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                >
                  <Pause className="h-3.5 w-3.5" />
                  Pause All
                </button>
                <button
                  onClick={handleStopTeam}
                  disabled={stopTeam.isPending}
                  className="flex items-center gap-1.5 rounded-lg border border-red-300 px-3 py-1.5 text-xs font-medium text-red-600 hover:bg-red-50 disabled:opacity-50"
                >
                  <Square className="h-3.5 w-3.5" />
                  Stop All
                </button>
              </>
            )}
            {team.status === TeamStatus.PAUSED && (
              <>
                <button
                  onClick={handleResumeTeam}
                  disabled={resumeTeam.isPending}
                  className="flex items-center gap-1.5 rounded-lg bg-slate-900 px-3 py-1.5 text-xs font-medium text-white hover:bg-slate-800 disabled:opacity-50"
                >
                  <Play className="h-3.5 w-3.5" />
                  Resume All
                </button>
                <button
                  onClick={handleStopTeam}
                  disabled={stopTeam.isPending}
                  className="flex items-center gap-1.5 rounded-lg border border-red-300 px-3 py-1.5 text-xs font-medium text-red-600 hover:bg-red-50 disabled:opacity-50"
                >
                  <Square className="h-3.5 w-3.5" />
                  Stop All
                </button>
              </>
            )}
            {(team.status === TeamStatus.COMPLETED || team.status === TeamStatus.FAILED) && (
              <button
                onClick={handleDeleteTeam}
                disabled={deleteTeam.isPending}
                className="flex items-center gap-1.5 rounded-lg border border-red-300 px-3 py-1.5 text-xs font-medium text-red-600 hover:bg-red-50 disabled:opacity-50"
              >
                <Trash2 className="h-3.5 w-3.5" />
                Delete
              </button>
            )}
          </div>
        </div>
        {team.description && (
          <p className="mt-2 text-sm text-slate-500">{team.description}</p>
        )}
      </div>

      {/* Main content area */}
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
        {/* Instance panes */}
        <div className="flex-1 overflow-auto p-4">
          {instances.length === 0 ? (
            <div className="flex h-full items-center justify-center">
              <div className="text-center">
                <Bot className="mx-auto mb-3 h-10 w-10 text-slate-300" />
                <p className="text-sm text-slate-500">No instances in this team</p>
              </div>
            </div>
          ) : (
            <div className={`grid gap-4 ${gridCols}`}>
              {instances.map((inst) => (
                <div key={inst.id} className="flex flex-col rounded-xl border border-slate-200 bg-white">
                  {/* Instance header */}
                  <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
                    <div className="flex items-center gap-2">
                      <div className={`h-2 w-2 rounded-full ${INSTANCE_STATUS_DOT[inst.status] ?? 'bg-slate-400'}`} />
                      <span className="text-sm font-medium text-slate-900">
                        {inst.agent?.name ?? 'Unknown'}
                      </span>
                      <span className="text-xs text-slate-400">{inst.model}</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      {inst.status === InstanceStatus.RUNNING && (
                        <>
                          <button
                            onClick={() => pauseInstance.mutate(inst.id)}
                            className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
                            title="Pause"
                          >
                            <Pause className="h-3.5 w-3.5" />
                          </button>
                          <button
                            onClick={() => stopInstance.mutate(inst.id)}
                            className="rounded p-1 text-slate-400 hover:bg-red-50 hover:text-red-600"
                            title="Stop"
                          >
                            <Square className="h-3.5 w-3.5" />
                          </button>
                        </>
                      )}
                      {inst.status === InstanceStatus.PAUSED && (
                        <button
                          onClick={() => resumeInstance.mutate({ id: inst.id })}
                          className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
                          title="Resume"
                        >
                          <Play className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Mini stats */}
                  <div className="flex items-center gap-3 border-b border-slate-100 px-4 py-1.5 text-xs text-slate-400">
                    <span className="flex items-center gap-1">
                      <Zap className="h-3 w-3" />
                      Turn {inst.currentTurn}/{inst.maxTurns}
                    </span>
                    <span className="flex items-center gap-1">
                      <DollarSign className="h-3 w-3" />
                      ${Number(inst.totalCostUsd).toFixed(2)}
                    </span>
                  </div>

                  {/* Terminal */}
                  <div className="p-2">
                    <InstanceTerminal instanceId={inst.id} status={inst.status} />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Bottom panel - activity/messages */}
        <div className="shrink-0 border-t border-slate-200">
          <div className="flex gap-1 border-b border-slate-100 px-4">
            <button
              onClick={() => setBottomTab('activity')}
              className={`flex items-center gap-1.5 border-b-2 px-3 py-2 text-xs font-medium transition-colors ${
                bottomTab === 'activity'
                  ? 'border-slate-900 text-slate-900'
                  : 'border-transparent text-slate-500 hover:text-slate-700'
              }`}
            >
              <Activity className="h-3.5 w-3.5" />
              Activity
              {activityEvents.length > 0 && (
                <span className="rounded-full bg-slate-200 px-1.5 py-0.5 text-xs text-slate-600">
                  {activityEvents.length}
                </span>
              )}
            </button>
            <button
              onClick={() => setBottomTab('messages')}
              className={`flex items-center gap-1.5 border-b-2 px-3 py-2 text-xs font-medium transition-colors ${
                bottomTab === 'messages'
                  ? 'border-slate-900 text-slate-900'
                  : 'border-transparent text-slate-500 hover:text-slate-700'
              }`}
            >
              <MessageSquare className="h-3.5 w-3.5" />
              Messages
              {messages && messages.length > 0 && (
                <span className="rounded-full bg-slate-200 px-1.5 py-0.5 text-xs text-slate-600">
                  {messages.length}
                </span>
              )}
            </button>
          </div>

          <div className="p-4">
            {bottomTab === 'activity' && (
              <TeamActivity events={activityEvents} />
            )}
            {bottomTab === 'messages' && (
              <TeamMessages
                messages={messages ?? []}
                instanceOptions={instanceOptions}
                onSendMessage={(data) => sendMessage.mutate(data)}
                isSending={sendMessage.isPending}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
